import { useInterval } from "@mantine/hooks";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import ky from "ky";
import { useEffect, useEffectEvent } from "react";

import { useLogger } from "@/sdk";

import { env } from "../../../config";

dayjs.extend(duration);
dayjs.extend(relativeTime);

const VERSION_CHECK_INTERVAL = dayjs.duration(5, "minutes").asMilliseconds();
const commitHashInBrowser = env.commitHash;

type BuildInfo = {
    date: string;
    props: {
        commitHash: string;
    };
};

/**
 * Polls the deployed build info and logs whether this client is on the latest build.
 * Runs once on mount and then every 5 minutes. Logs commit hash, up-to-date status, and
 * time since the latest build so we can build dashboards (e.g. % of experts on latest,
 * average time to fetch new code) and report to the business. Optionally invokes
 * a callback when a newer build is available, for future use (e.g. in-app update alerts).
 *
 * @param updateAvailableCallback - Called when the client is not running the latest build. Optional.
 */
export const useTrackVersion = (updateAvailableCallback?: () => void) => {
    const logger = useLogger();

    const checkVersion = useEffectEvent(async () => {
        try {
            const json = await ky.get("/build.json").json<BuildInfo>();

            const commitHashOfLatestBuild = json.props.commitHash;

            const dateOfLatestBuild = dayjs(json.date);
            if (!dateOfLatestBuild.isValid()) {
                logger.warn({ buildDate: json.date }, "Invalid build date in build.json");
            }

            const isBrowserRunningLatestBuild = commitHashInBrowser === commitHashOfLatestBuild;
            if (!isBrowserRunningLatestBuild) updateAvailableCallback?.();

            const timeSinceLatestBuild = dateOfLatestBuild.isValid() ? dayjs().diff(dateOfLatestBuild) : null;
            const timeSinceLatestBuildFormatted =
                timeSinceLatestBuild === null ? null : dayjs.duration(timeSinceLatestBuild).humanize();

            logger.info(
                {
                    // commitHash is already included in the default log context
                    commitHashOfLatestBuild,
                    isBrowserRunningLatestBuild,
                    timeSinceLatestBuild,
                    timeSinceLatestBuildFormatted,
                },
                "Version tracking",
            );
        } catch (cause) {
            logger.warn({ cause }, "Version tracking failed");
        }
    });

    // Check version on mount...
    useEffect(() => {
        void checkVersion();
    }, []);

    // ...and then every interval
    // oxlint-disable-next-line react-hooks/rules-of-hooks
    useInterval(checkVersion, VERSION_CHECK_INTERVAL, { autoInvoke: true });
};
